import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) { toast.success(`Welcome to ${BRAND.name} ERP`); nav("/"); }
    else toast.error(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          alt={BRAND.name}
          src="https://images.pexels.com/photos/18513512/pexels-photo-18513512.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1440"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-background/95 via-background/60 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt={BRAND.name} className="h-14 w-14 rounded-lg bg-white p-1.5 object-contain ring-1 ring-black/10 shadow" />
            <div>
              <div className="font-display text-xl font-semibold tracking-tight">{BRAND.name}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{BRAND.tagline}</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-5xl xl:text-6xl leading-[1.05] font-semibold tracking-tight">
              Build studios,<br />not spreadsheets.
            </h1>
            <p className="mt-6 text-muted-foreground max-w-md text-base leading-relaxed">
              An enterprise operating system for architects, interior designers and construction teams —
              from lead to handover, in one calm workspace.
            </p>
            <div className="mt-8 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#8DC63F" }} />
              <span className="uppercase tracking-[0.2em]">CRM • Projects • BOQ • Site • Finance • HR</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={BRAND.logoUrl} alt={BRAND.name} className="h-11 w-11 rounded-md bg-white p-1 object-contain ring-1 ring-black/10" />
            <div>
              <div className="font-display text-lg font-semibold tracking-tight">{BRAND.name}</div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground">{BRAND.tagline}</div>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Sign in</div>
            <h2 className="font-display text-3xl font-semibold">Welcome back.</h2>
            <p className="text-sm text-muted-foreground mt-2">Use your studio credentials to continue.</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
                <a href="/forgot-password" data-testid="login-forgot" className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary">Forgot?</a>
              </div>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
            >
              {busy ? "Signing in…" : "Sign in to Studio"}
            </Button>
          </form>


        </div>
      </div>
    </div>
  );
}
